import { RuntsBadge } from "@/components/RuntsBadge";
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, FileUp, LineChart, ShieldAlert, SlidersHorizontal, ShieldCheck,
  MessageSquare, Gauge, Sparkles, CheckCircle2, Upload, FileText, Save,
} from "lucide-react";
import api, { formatApiError, API } from "@/pages/cauzioni/cauApi";
import CauShell from "@/pages/cauzioni/CauShell";
import { EsitoBadge } from "@/pages/cauzioni/Badges";
import { euro, TIPI_GARANZIA, FORME_GIURIDICHE, PARERE } from "@/pages/cauzioni/labels";
import ClienteCollegati from "@/components/ClienteCollegati";
import CauBancheDati from "@/pages/cauzioni/CauBancheDati";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { key: "anagrafica", label: "Anagrafica & Operazione", icon: Building2 },
  { key: "documenti", label: "Documenti", icon: FileUp },
  { key: "analisi", label: "Analisi finanziaria", icon: LineChart },
  { key: "reputazione", label: "Reputazione", icon: ShieldAlert },
  { key: "valutazione", label: "Valutazione & Scoring", icon: SlidersHorizontal },
  { key: "mitigazione", label: "Mitigazione", icon: ShieldCheck },
  { key: "parere", label: "Parere intermediario", icon: MessageSquare },
  { key: "esito", label: "Esito", icon: Gauge },
];

export default function CauWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [scoring, setScoring] = useState(null);

  const load = useCallback(() => api.get(`/pratiche/${id}`).then((r) => setP(r.data)), [id]);
  useEffect(() => { load().catch(() => { toast.error("Pratica non trovata"); nav("/cauzioni-pro"); }); /* eslint-disable-next-line */ }, [id]);

  const patch = async (payload, msg) => {
    setBusy(true);
    try { const { data } = await api.put(`/pratiche/${id}`, payload); setP(data); if (msg) toast.success(msg); return data; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!p) return <CauShell><div className="p-16 text-center text-slate-400">Caricamento…</div></CauShell>;

  const StepIcon = STEPS[step].icon;

  return (
    <CauShell>
      <div className="mx-auto max-w-[1400px] px-6 py-6" data-testid="cau-wizard-page">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => nav("/cauzioni-pro")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="wizard-back"><ArrowLeft size={15} /> Pratiche</button>
          <div className="flex items-center gap-3">
            <EsitoBadge esito={p.esitoFinale} />
            <div className="text-sm text-slate-500">Score: <span className="font-semibold text-slate-900">{p.scoreTotale != null ? `${p.scoreTotale}/100` : "—"}</span></div>
            <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => nav(`/cauzioni-pro/pratica/${id}/relazione`)} data-testid="goto-relazione"><FileText size={14} className="mr-1.5" /> Relazione</Button>
          </div>
        </div>

        <div className="mb-6">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{p.cliente?.ragioneSociale || "Pratica"}</h1>
          <RuntsBadge cf={p.cliente?.codiceFiscale || p.cliente?.partitaIva} testid="cau-runts-badge" />
          <p className="text-sm text-slate-500">{TIPI_GARANZIA[p.tipoGaranzia]} · {euro(p.importo)} · {p.durataMesi || 0} mesi</p>
        </div>

        <div className="mb-6">
          <ClienteCollegati currentModule="cau" currentId={id}
            anagraficaId={p.anagraficaId} nome={p.cliente?.ragioneSociale} piva={p.cliente?.partitaIva} />
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-6">
          <nav className="space-y-1">
            {STEPS.map((s, i) => {
              const Ic = s.icon;
              return (
                <button key={s.key} onClick={() => setStep(i)} data-testid={`wizard-step-${s.key}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm text-left transition-colors ${step === i ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Ic size={16} /> {s.label}
                </button>
              );
            })}
          </nav>

          <div className="bg-white border border-slate-200 rounded-sm p-6 min-h-[420px]">
            <div className="flex items-center gap-2 mb-5">
              <StepIcon size={18} className="text-slate-700" />
              <h2 className="font-display text-xl font-bold text-slate-900">{STEPS[step].label}</h2>
            </div>

            {step === 0 && <><CauBancheDati p={p} onUpdate={load} /><StepAnagrafica key={p.bancheDati?.eseguitoIl || "x"} p={p} patch={patch} busy={busy} /></>}
            {step === 1 && <StepDocumenti p={p} id={id} reload={load} busy={busy} setBusy={setBusy} patch={patch} />}
            {step === 2 && <StepAnalisi p={p} id={id} busy={busy} setBusy={setBusy} reload={load} />}
            {step === 3 && <StepReputazione p={p} id={id} patch={patch} busy={busy} setBusy={setBusy} reload={load} />}
            {step === 4 && <StepValutazione p={p} id={id} patch={patch} busy={busy} setBusy={setBusy} reload={load} setScoring={setScoring} scoring={scoring} />}
            {step === 5 && <StepMitigazione p={p} id={id} patch={patch} busy={busy} setBusy={setBusy} reload={load} />}
            {step === 6 && <StepParere p={p} patch={patch} busy={busy} />}
            {step === 7 && <StepEsito p={p} id={id} setScoring={setScoring} scoring={scoring} nav={nav} busy={busy} setBusy={setBusy} reload={load} />}

            <div className="mt-8 flex justify-between border-t border-slate-200 pt-5">
              <Button variant="outline" className="rounded-sm" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="step-prev">Indietro</Button>
              {step < STEPS.length - 1 && <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => setStep((s) => s + 1)} data-testid="step-next">Avanti</Button>}
            </div>
          </div>
        </div>
      </div>
    </CauShell>
  );
}

function Lbl({ children }) { return <label className="text-sm font-medium text-slate-700">{children}</label>; }

function StepAnagrafica({ p, patch, busy }) {
  const [c, setC] = useState(p.cliente || {});
  const [op, setOp] = useState({ tipoGaranzia: p.tipoGaranzia, oggettoGaranzia: p.oggettoGaranzia, enteBeneficiario: p.enteBeneficiario, importo: p.importo, durataMesi: p.durataMesi, premioAnnuale: p.premioAnnuale });
  const uc = (k, v) => setC({ ...c, [k]: v });
  const uo = (k, v) => setOp({ ...op, [k]: v });
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5 sm:col-span-2"><Lbl>Ragione sociale</Lbl><Input value={c.ragioneSociale || ""} onChange={(e) => uc("ragioneSociale", e.target.value)} data-testid="wz-ragione" /></div>
        <div className="space-y-1.5"><Lbl>Partita IVA</Lbl><Input value={c.partitaIva || ""} onChange={(e) => uc("partitaIva", e.target.value)} data-testid="wz-piva" /></div>
        <div className="space-y-1.5"><Lbl>Codice fiscale</Lbl><Input value={c.codiceFiscale || ""} onChange={(e) => uc("codiceFiscale", e.target.value)} /></div>
        <div className="space-y-1.5"><Lbl>Forma giuridica</Lbl>
          <Select value={c.formaGiuridica || ""} onValueChange={(v) => uc("formaGiuridica", v)}>
            <SelectTrigger data-testid="wz-forma"><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>{FORME_GIURIDICHE.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Lbl>Settore</Lbl><Input value={c.settore || ""} onChange={(e) => uc("settore", e.target.value)} /></div>
        <div className="space-y-1.5 sm:col-span-2"><Lbl>Sito web (per profilo AI)</Lbl><Input value={c.sitoWeb || ""} onChange={(e) => uc("sitoWeb", e.target.value)} placeholder="https://www.cliente.it" data-testid="wz-sitoweb" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
        <div className="space-y-1.5"><Lbl>Tipo garanzia</Lbl>
          <Select value={op.tipoGaranzia} onValueChange={(v) => uo("tipoGaranzia", v)}>
            <SelectTrigger data-testid="wz-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TIPI_GARANZIA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Lbl>Ente beneficiario</Lbl><Input value={op.enteBeneficiario || ""} onChange={(e) => uo("enteBeneficiario", e.target.value)} /></div>
        <div className="space-y-1.5 sm:col-span-2"><Lbl>Oggetto della garanzia</Lbl><Textarea rows={2} value={op.oggettoGaranzia || ""} onChange={(e) => uo("oggettoGaranzia", e.target.value)} /></div>
        <div className="space-y-1.5"><Lbl>Importo (€)</Lbl><Input type="number" value={op.importo ?? ""} onChange={(e) => uo("importo", e.target.value)} data-testid="wz-importo" /></div>
        <div className="space-y-1.5"><Lbl>Durata (mesi)</Lbl><Input type="number" value={op.durataMesi ?? ""} onChange={(e) => uo("durataMesi", e.target.value)} data-testid="wz-durata" /></div>
        <div className="space-y-1.5"><Lbl>Premio annuale (€)</Lbl><Input type="number" value={op.premioAnnuale ?? ""} onChange={(e) => uo("premioAnnuale", e.target.value)} /></div>
      </div>
      <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={busy} data-testid="wz-save-anagrafica"
        onClick={() => patch({ cliente: c, ...op, importo: Number(op.importo) || 0, durataMesi: Number(op.durataMesi) || 0, premioAnnuale: Number(op.premioAnnuale) || 0 }, "Dati salvati")}>
        <Save size={15} className="mr-1.5" /> Salva dati
      </Button>
    </div>
  );
}

function StepDocumenti({ p, id, reload, busy, setBusy }) {
  const upload = async (tipo, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("tipoDocumento", tipo); fd.append("file", file);
    try { await api.post(`/pratiche/${id}/documenti/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Documento caricato"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="documenti-list">
      {(p.documenti || []).map((d) => (
        <div key={d.id} className="flex items-center justify-between border border-slate-200 rounded-sm px-4 py-3">
          <div>
            <div className="text-sm font-medium text-slate-800">{d.label} {d.obbligatorio && <span className="text-rose-500 text-xs">(obbligatorio)</span>}</div>
            <div className="text-xs text-slate-400">{d.caricato ? `Caricato: ${d.fileName}` : "Non caricato"}</div>
          </div>
          <div className="flex items-center gap-2">
            {d.caricato && <CheckCircle2 size={18} className="text-emerald-500" />}
            <label className="cursor-pointer">
              <span className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-sm px-3 py-1.5 hover:bg-slate-50" data-testid={`upload-${d.tipoDocumento}`}><Upload size={14} /> {d.caricato ? "Sostituisci" : "Carica"}</span>
              <input type="file" className="hidden" disabled={busy} onChange={(e) => upload(d.tipoDocumento, e.target.files?.[0])} />
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

function StepAnalisi({ p, id, busy, setBusy, reload }) {
  const df = p.datiFinanziari || {};
  const db = p.datiBando || {};
  const runFin = async () => {
    setBusy(true);
    try { await api.post(`/pratiche/${id}/analisi-finanziaria`); toast.success("Dati finanziari estratti dall'AI"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runBando = async () => {
    setBusy(true);
    try { await api.post(`/pratiche/${id}/analisi-bando`); toast.success("Requisiti della garanzia estratti dall'AI"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const capitalPct = df.gradoCapitalizzazione != null ? `${(df.gradoCapitalizzazione * 100).toFixed(1)}%` : null;
  const de = (df.debitiFinanziari != null && df.patrimonioNetto) ? (df.debitiFinanziari / df.patrimonioNetto).toFixed(2) : null;
  const rows = [
    ["Anno bilancio", df.annoBilancio], ["Patrimonio netto", df.patrimonioNetto != null ? euro(df.patrimonioNetto) : null],
    ["Totale attivo", df.totaleAttivo != null ? euro(df.totaleAttivo) : null], ["Rimanenze", df.rimanenze != null ? euro(df.rimanenze) : null],
    ["Attivo corrente", df.attivoCorrente != null ? euro(df.attivoCorrente) : null], ["Passivo corrente", df.passivoCorrente != null ? euro(df.passivoCorrente) : null],
    ["Disponibilità liquide", df.disponibilitaLiquide != null ? euro(df.disponibilitaLiquide) : null], ["Indice liquidità corrente", df.indiceLiquidita],
    ["Liquidità primaria (acid test)", df.acidTest], ["Grado capitalizzazione (PN/Attivo)", capitalPct],
    ["Debiti finanziari", df.debitiFinanziari != null ? euro(df.debitiFinanziari) : null], ["Leverage D/E (Debiti fin./PN)", de],
    ["Debiti totali", df.debitiTotali != null ? euro(df.debitiTotali) : null], ["EBITDA", df.ebitda != null ? euro(df.ebitda) : null],
    ["Ricavi", df.ricavi != null ? euro(df.ricavi) : null], ["Centrale Rischi", df.esposizioneCentraleRischi],
  ];
  const boolLabel = (v) => (v === true ? "Sì" : v === false ? "No" : "—");
  const TIPO_CAU = { provvisoria: "Provvisoria", definitiva: "Definitiva", anticipazione: "Anticipazione", buona_esecuzione: "Buona esecuzione", altro: "Altro" };
  const bandoRows = [
    ["Tipo cauzione", db.tipoCauzione ? (TIPO_CAU[db.tipoCauzione] || db.tipoCauzione) : null],
    ["Importo garantito richiesto", db.importoGarantito != null ? euro(db.importoGarantito) : null],
    ["% dell'importo", db.percentualeImporto != null ? `${db.percentualeImporto}%` : null],
    ["Durata richiesta", db.durataMesi != null ? `${db.durataMesi} mesi` : null],
    ["Scadenza", db.scadenza],
    ["Ente beneficiario", db.enteBeneficiario],
    ["Pagamento a prima richiesta", db.pagamentoPrimaRichiesta != null ? boolLabel(db.pagamentoPrimaRichiesta) : null],
    ["Rinuncia beneficio escussione (art.1944)", db.rinunciaBeneficioEscussione != null ? boolLabel(db.rinunciaBeneficioEscussione) : null],
    ["Rinuncia art.1957", db.rinunciaArt1957 != null ? boolLabel(db.rinunciaArt1957) : null],
    ["Coobbligazione richiesta dal bando", db.coobbligazioneRichiesta != null ? boolLabel(db.coobbligazioneRichiesta) : null],
    ["Fideiussori richiesti", db.fideiussoriRichiesti],
  ];
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <p className="text-sm text-slate-500">Estrae automaticamente i dati di bilancio dai documenti caricati (OCR + AI). Carica prima almeno un bilancio o documento contabile.</p>
        <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={runFin} disabled={busy} data-testid="run-analisi"><Sparkles size={15} className="mr-1.5" /> {busy ? "Analisi in corso…" : "Estrai dati finanziari (AI)"}</Button>
        {df.generatoIl && (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 border-t border-slate-100 pt-4" data-testid="dati-finanziari">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm border-b border-slate-50 py-1"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v ?? "—"}</span></div>
            ))}
            {df.note && <div className="sm:col-span-2 text-xs text-slate-500 mt-2">{df.note}</div>}
          </div>
        )}
      </div>

      <div className="space-y-4 border-t border-slate-200 pt-5">
        <div className="flex items-center gap-2"><FileText size={16} className="text-slate-700" /><h3 className="font-display font-bold text-slate-900">Requisiti della garanzia (bando / capitolato / disciplinare)</h3></div>
        <p className="text-sm text-slate-500">Estrae dai documenti di gara oggetto, importo, durata, tipo cauzione, clausole (prima richiesta, rinunce) ed eventuale richiesta di coobbligazione. Carica prima il bando o il capitolato/disciplinare.</p>
        <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={runBando} disabled={busy} data-testid="run-analisi-bando"><Sparkles size={15} className="mr-1.5" /> {busy ? "Analisi in corso…" : "Analizza requisiti garanzia (AI)"}</Button>
        {db.generatoIl && (
          <div className="space-y-2 border-t border-slate-100 pt-4" data-testid="dati-bando">
            {db.oggetto && <div className="text-sm"><span className="text-slate-500">Oggetto: </span><span className="text-slate-800">{db.oggetto}</span></div>}
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {bandoRows.map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm border-b border-slate-50 py-1"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v ?? "—"}</span></div>
              ))}
            </div>
            {db.penali && <div className="text-xs text-slate-600 mt-1"><b>Penali:</b> {db.penali}</div>}
            {db.requisiti && <div className="text-xs text-slate-600"><b>Requisiti:</b> {db.requisiti}</div>}
            {(db.coerenzaImporto === false || db.coerenzaDurata === false) && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-1" data-testid="bando-incongruenza">
                ⚠ Incongruenza con i dati di pratica:
                {db.coerenzaImporto === false && " importo garantito diverso da quello inserito;"}
                {db.coerenzaDurata === false && " durata diversa da quella inserita;"}
                {" verifica prima di procedere."}
              </div>
            )}
            {db.note && <div className="text-xs text-slate-500 mt-1">{db.note}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function StepReputazione({ p, id, patch, busy, setBusy, reload }) {
  const rep = p.reputazione || {};
  const search = async () => {
    setBusy(true);
    try { await api.post(`/pratiche/${id}/reputazione/search`); toast.success("Analisi reputazionale generata"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={search} disabled={busy} data-testid="run-reputazione"><Sparkles size={15} className="mr-1.5" /> {busy ? "Analisi…" : "Genera analisi reputazionale (AI)"}</Button>
      <p className="text-xs text-slate-500">Usa i dati di VIES e Registro Imprese, il sito web del cliente e la conoscenza del modello AI (non una ricerca web in tempo reale): i risultati vanno verificati.</p>
      {rep.esitiWeb && <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700 whitespace-pre-wrap" data-testid="reputazione-esiti">{rep.esitiWeb}</div>}
      {rep.fonteRicerca && <p className="text-[11px] text-slate-400" data-testid="reputazione-fonte">Fonte: {rep.fonteRicerca}</p>}
      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div><div className="text-sm font-medium text-slate-800">Alert reputazionale</div><div className="text-xs text-slate-400">Segnala criticità da approfondire</div></div>
        <Switch checked={!!rep.alert} onCheckedChange={(v) => patch({ reputazione: { alert: v } })} data-testid="rep-alert" />
      </div>
      <div className="flex items-center justify-between">
        <div><div className="text-sm font-medium text-slate-800">Validato manualmente</div><div className="text-xs text-slate-400">Alert verificato dall'operatore</div></div>
        <Switch checked={!!rep.validatoManualmente} onCheckedChange={(v) => patch({ reputazione: { validatoManualmente: v } })} data-testid="rep-validato" />
      </div>
    </div>
  );
}

function StepValutazione({ p, id, patch, busy, setBusy, reload, setScoring }) {
  const [val, setVal] = useState(p.valutazione || {});
  const AREAS = [
    ["reputazioneScore", "Reputazione e pregiudizievoli", 20], ["solvibilitaScore", "Solidità patrimoniale", 20],
    ["liquiditaScore", "Liquidità", 15], ["redditivitaScore", "Redditività", 15],
    ["coerenzaScore", "Coerenza operazione / oggetto sociale", 10], ["rischioTecnicoScore", "Rischio tecnico della commessa", 10],
    ["parereIntermediarioScore", "Parere intermediario", 10],
  ];
  const suggest = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/pratiche/${id}/scoring/suggerisci`); setVal((v) => ({ ...v, ...data.suggerimenti })); toast.success("Punteggi suggeriti dall'AI" + (data.note ? ": " + data.note.slice(0, 80) : "")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runScoring = async () => {
    await patch({ valutazione: val });
    setBusy(true);
    try { const { data } = await api.post(`/pratiche/${id}/scoring`); setScoring(data); await reload(); toast.success(`Score ${data.scoreTotale}/100`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const tot = AREAS.reduce((s, [k, , mx]) => s + Math.max(0, Math.min(Number(val[k]) || 0, mx)), 0);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Assegna un punteggio per area (max indicato). Totale su 100.</p>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={suggest} disabled={busy} data-testid="suggest-scoring"><Sparkles size={14} className="mr-1.5" /> Suggerisci con AI</Button>
      </div>
      <div className="space-y-2">
        {AREAS.map(([k, label, mx]) => (
          <div key={k} className="flex items-center gap-3">
            <div className="flex-1 text-sm text-slate-700">{label} <span className="text-slate-400">/ {mx}</span></div>
            <Input type="number" min={0} max={mx} value={val[k] ?? ""} onChange={(e) => setVal({ ...val, [k]: e.target.value })} className="w-24" data-testid={`score-${k}`} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div className="text-lg font-display font-extrabold text-slate-900">Totale: {Math.round(tot)}/100</div>
        <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={runScoring} disabled={busy} data-testid="run-scoring"><Gauge size={15} className="mr-1.5" /> Calcola scoring</Button>
      </div>
    </div>
  );
}

function StepMitigazione({ p, id, patch, busy, setBusy, reload }) {
  const [m, setM] = useState(p.mitigazione || { coobbligazione: true });
  const dc = p.datiCoobbligato || {};
  const runCoob = async () => {
    setBusy(true);
    try { await api.post(`/pratiche/${id}/analisi-coobbligato`); toast.success("Consistenza del coobbligato valutata dall'AI"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const CAP = {
    sufficiente: { label: "Capienza sufficiente", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    insufficiente: { label: "Capienza insufficiente", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    da_integrare: { label: "Capienza da integrare", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const cap = CAP[dc.giudizioCapienza];
  const coobRows = [
    ["Nominativo", dc.nominativo],
    ["Tipo soggetto", dc.tipoSoggetto === "persona_fisica" ? "Persona fisica" : dc.tipoSoggetto === "persona_giuridica" ? "Persona giuridica" : null],
    ["Reddito annuo", dc.redditoAnnuo != null ? euro(dc.redditoAnnuo) : null],
    ["Patrimonio netto", dc.patrimonioNetto != null ? euro(dc.patrimonioNetto) : null],
    ["Capienza stimata", dc.capienzaStimata != null ? euro(dc.capienzaStimata) : null],
    ["Immobili", dc.immobili],
    ["Altre partecipazioni/garanzie", dc.altrePartecipazioniGaranzie],
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border border-slate-200 rounded-sm px-4 py-3">
        <div><div className="text-sm font-medium text-slate-800">Coobbligazione del Legale Rappresentante</div><div className="text-xs text-slate-400">Consigliata in rapporto a importo e patrimonio netto</div></div>
        <Switch checked={!!m.coobbligazione} onCheckedChange={(v) => setM({ ...m, coobbligazione: v })} data-testid="mit-coobbligazione" />
      </div>
      {!m.coobbligazione && (
        <div className="space-y-1.5"><Lbl>Deroga motivata (obbligatoria se manca la coobbligazione)</Lbl>
          <Textarea rows={3} value={m.derogaMotivata || ""} onChange={(e) => setM({ ...m, derogaMotivata: e.target.value })} data-testid="mit-deroga" /></div>
      )}
      <div className="space-y-1.5"><Lbl>Garanzie accessorie</Lbl><Textarea rows={2} value={m.garanzieAccessorie || ""} onChange={(e) => setM({ ...m, garanzieAccessorie: e.target.value })} /></div>
      <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={() => patch({ mitigazione: m }, "Mitigazione salvata")} data-testid="save-mitigazione"><Save size={15} className="mr-1.5" /> Salva</Button>

      <div className="space-y-3 border-t border-slate-200 pt-5">
        <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-slate-700" /><h3 className="font-display font-bold text-slate-900">Consistenza del coobbligato / garante</h3></div>
        <p className="text-sm text-slate-500">Valuta la capienza del socio da coobbligare dai suoi documenti (bilancio o dichiarazione dei redditi/Modello Unico). Carica prima la documentazione del coobbligato.</p>
        <Button variant="outline" className="rounded-sm" onClick={runCoob} disabled={busy} data-testid="run-analisi-coobbligato"><Sparkles size={15} className="mr-1.5" /> {busy ? "Analisi in corso…" : "Valuta consistenza coobbligato (AI)"}</Button>
        {dc.generatoIl && (
          <div className="space-y-2 border-t border-slate-100 pt-4" data-testid="dati-coobbligato">
            {cap && <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-sm border ${cap.cls}`} data-testid="coob-giudizio">{cap.label}</span>}
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {coobRows.map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm border-b border-slate-50 py-1"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v ?? "—"}</span></div>
              ))}
            </div>
            {dc.motivazione && <div className="text-xs text-slate-600 mt-1"><b>Motivazione:</b> {dc.motivazione}</div>}
            {dc.note && <div className="text-xs text-slate-500">{dc.note}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function StepParere({ p, patch, busy }) {
  const [par, setPar] = useState(p.parere || {});
  return (
    <div className="space-y-4">
      <div className="space-y-1.5"><Lbl>Giudizio dell'intermediario</Lbl>
        <Select value={par.giudizio || ""} onValueChange={(v) => setPar({ ...par, giudizio: v })}>
          <SelectTrigger data-testid="parere-giudizio"><SelectValue placeholder="Seleziona giudizio" /></SelectTrigger>
          <SelectContent>{Object.entries(PARERE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select></div>
      <div className="space-y-1.5"><Lbl>Motivazione</Lbl><Textarea rows={4} value={par.motivazione || ""} onChange={(e) => setPar({ ...par, motivazione: e.target.value })} data-testid="parere-motivazione" /></div>
      <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={() => patch({ parere: par }, "Parere salvato")} data-testid="save-parere"><Save size={15} className="mr-1.5" /> Salva parere</Button>
    </div>
  );
}

function StepEsito({ p, id, scoring, setScoring, nav, busy, setBusy, reload }) {
  const s = scoring;
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/pratiche/${id}/scoring`); setScoring(data); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const profilo = async () => {
    setBusy(true);
    try { await api.post(`/pratiche/${id}/profilo-cliente/genera`); toast.success("Profilo cliente generato dal web"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div className="text-5xl font-display font-extrabold text-slate-900" data-testid="esito-score">{p.scoreTotale != null ? p.scoreTotale : "—"}<span className="text-2xl text-slate-400">/100</span></div>
        <EsitoBadge esito={p.esitoFinale} className="text-sm px-3 py-1" />
        <Button variant="outline" size="sm" className="rounded-sm" onClick={run} disabled={busy} data-testid="recompute-scoring"><Gauge size={14} className="mr-1.5" /> Ricalcola</Button>
      </div>
      {(s?.criticita?.length || p.esitoFinale) && (
        <div className="border border-slate-200 rounded-sm p-4">
          <div className="text-sm font-medium text-slate-800 mb-2">Criticità rilevate</div>
          {(s?.criticita || []).length === 0 ? <div className="text-sm text-emerald-600">Nessuna criticità bloccante.</div> :
            <ul className="list-disc pl-5 text-sm text-amber-700 space-y-1">{s.criticita.map((c, i) => <li key={i}>{c}</li>)}</ul>}
        </div>
      )}
      {p.profiloCliente && <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700 whitespace-pre-wrap" data-testid="profilo-cliente">{p.profiloCliente}</div>}
      <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        <Button variant="outline" className="rounded-sm" onClick={profilo} disabled={busy} data-testid="genera-profilo"><Sparkles size={15} className="mr-1.5" /> Genera profilo cliente (web)</Button>
        <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => nav(`/cauzioni-pro/pratica/${id}/relazione`)} data-testid="vai-relazione"><FileText size={15} className="mr-1.5" /> Vai alla relazione</Button>
      </div>
    </div>
  );
}
